<template>
  <b-card
      v-if="
      $can('add_bid', 'Offres') ||
      $can('quote', 'Offres') ||
      $can('quote', 'Offres')
    "
      class="border-primary"
      no-body
  >
    <b-card-body>
      <!-- expense button -->
      <expense-handler
          v-if="$can('add_bid_request', 'Offres')"
          :opportunityId="bidData.uid"
          class="mb-2"
          @expenseAdded="expenseHandled"
      />
      <!-- budget tracking -->
      <b-button
          v-if="$can('quote', 'Offres')"
          :to="{
          name: 'budget-quote-add',
          params: { id: bidData.uid },
        }"
          block
          class="mb-2"
          variant="warning"
      >
        Devis
      </b-button>

      <!-- Client Validation Section -->
      <div v-if="!isLost && !bidData.is_won && requiresClientValidation">
        <!-- Notify Customer Button -->
        <b-button
            v-if="!hasClientValidationPending"
            :disabled="isNotifying"
            block
            class="mb-2"
            variant="info"
            @click="handleNotifyCustomer"
        >
          <b-spinner v-if="isNotifying" class="mr-1" small/>
          Notifier le client
        </b-button>

        <!-- Validation Status Badge -->
        <b-alert v-if="hasClientValidationPending" class="mb-2" show variant="warning">
          <div class="alert-body">
            <h6 class="alert-heading mb-50">En attente de validation client</h6>
            <div class="d-flex align-items-center">
              <small class="text-muted">Notifié le {{ formatDate(validationNotifiedAt) }}</small>
              <b-button
                  :disabled="isNotifying"
                  size="sm"
                  :variant="validationStatusVariant"
                  @click="handleNotifyCustomer"
                  class="p-1 ml-1"
              >
                <b-spinner v-if="isNotifying" class="mr-1" small/>
                <feather-icon v-else icon="RefreshCwIcon" size="12" />
              </b-button>
            </div>
          </div>
        </b-alert>

        <!-- Rejection Comment -->
        <b-alert v-if="validationStatus === 'rejected'" class="mb-2" show variant="danger">
          <div class="alert-body">
            <h6 class="alert-heading mb-50">Motif du refus</h6>
            <p class="mb-50">{{ validationRejectionComment }}</p>
            <b-button
                :disabled="isNotifying"
                size="sm"
                variant="outline-danger"
                @click="handleNotifyCustomer"
            >
              <b-spinner v-if="isNotifying" class="mr-50" small/>
              Relancer le client
            </b-button>
          </div>
        </b-alert>
      </div>

      <!-- set opportunity as project (for non-validation types) -->
      <b-button
          v-if="!isLost && !bidData.is_won && !requiresClientValidation"
          block
          variant="success"
          @click="toggleAsWin"
      >
        Générer un Projet
      </b-button>

      <!-- go to project -->
      <b-button
          v-if="bidData.is_won"
          :to="{ name: 'projects-view', params: { id: bidData.uid } }"
          block
          variant="success"
      >
        Accéder au Projet
      </b-button>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BAlert,
  BButton,
  BCard,
  BCardBody,
  BSpinner
} from "bootstrap-vue";
import ExpenseHandler from "@/components/expenses/ExpenseHandler.vue";
import useBidDetails from './useBidDetails';
import useQuoteValidation from '@/composables/useQuoteValidation';
import {computed, ref, toRefs, watch} from '@vue/composition-api';
import {useToast} from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import {formatDate} from "@core/utils/filter";
import {VALIDATION_REQUIRED_TYPES} from "@/constants/bid";

export default {
  props: {
    bidData: {
      type: Object,
      required: true,
    },
    projectUpdated: Boolean,
  },
  components: {
    BCard,
    BCardBody,
    BButton,
    BAlert,
    BSpinner,
    ExpenseHandler,
  },
  methods: {
    expenseHandled() {
      this.$emit("bidUpdated");
    },
  },
  setup(props, {emit}) {
    const {
      toggleAsWin,
      isLost
    } = useBidDetails(toRefs(props), emit);

    const toast = useToast();

    const {notifyCustomer, getValidationStatus, isLoading: isNotifying} = useQuoteValidation();

    const validationStatus = ref(null);
    const validationNotifiedAt = ref(null);
    const validationRejectionComment = ref(null);

    // Types requiring client validation

    const requiresClientValidation = computed(() => {
      return VALIDATION_REQUIRED_TYPES().includes(props.bidData?.type?.toLowerCase());
    });

    const hasClientValidationPending = computed(() => {
      return props.bidData?.inventory?.client_validation_status === 'pending_client_validation';
    });

    const validationStatusLabel = computed(() => {
      switch (validationStatus.value) {
        case 'pending':
          return 'En attente';
        case 'validated':
          return 'Validé';
        case 'rejected':
          return 'Rejeté';
        default:
          return 'En attente';
      }
    });

    const validationStatusVariant = computed(() => {
      switch (validationStatus.value) {
        case 'pending':
          return 'warning';
        case 'validated':
          return 'success';
        case 'rejected':
          return 'danger';
        default:
          return 'secondary';
      }
    });

    const handleNotifyCustomer = async () => {
      if (!props.bidData?.budget_state) {
        console.error('Budget tracking ID non trouvé');
        toast({
          component: ToastificationContent,
          props: {
            title: "Aucun devis n'a été établi pour cette offre.",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      try {
        await notifyCustomer(props.bidData.uid);
        emit('bidUpdated');
      } catch (error) {
        console.error('Erreur lors de la notification:', error);
      }
    };

    // Charger le statut de validation si nécessaire
    watch(
        () => hasClientValidationPending.value,
        async (isPending) => {
          if (isPending && props.bidData?.budget_state) {
            try {
              const {resource: status} = await getValidationStatus(props.bidData.uid);

              validationStatus.value = status.status;
              validationNotifiedAt.value = status.notified_at;
              validationRejectionComment.value = status.rejection_comment;
            } catch (error) {
              console.error('Erreur lors du chargement du statut:', error);
            }
          }
        },
        {immediate: true}
    );

    return {
      formatDate,
      toggleAsWin,
      isLost,
      requiresClientValidation,
      hasClientValidationPending,
      validationStatus,
      validationStatusLabel,
      validationStatusVariant,
      validationNotifiedAt,
      validationRejectionComment,
      isNotifying,
      handleNotifyCustomer,
    };
  },
};
</script>

<style></style>
